/**
 * Shared domain-event feed state for the whole shell.
 *
 * One provider owns the initial fetch and a single SSE subscription so the
 * right rail, the TopBar unread badge, and any dashboard widget all read from
 * the same live timeline (rather than each opening its own connection).
 *
 * It also owns the single virtual-filter model (scope dimension + drill-down
 * key + time horizon + severity). The feed is one generic widget: "views" like Booking,
 * Customer, or Operator are just filter changes on this shared state, and My
 * Feed (the default) is the role-aware aggregate resolved server-side.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { api } from "@/services/api";
import { useToastOptional } from "@/context/ToastContext";
import { useUserOptional } from "@/auth/UserContext";
import {
  applyFeedReview,
  feedReviewToastTitle,
  restoreFeedReview,
  snapshotFeedEvents,
} from "@/lib/feedReviewState";
import { emitFeedReviewUndo } from "@/features/feed/feedReviewUndo";
import { useViewerBookingScope } from "@/hooks/useViewerBookingScope";
import { hasViewerScope } from "@/lib/viewerScopeFilters";
import { useTheme } from "@/context/ThemeContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { playNotificationSoundForEvent } from "@/lib/notificationSound";
import {
  KAI_FEED_FILTERS_EVENT,
  feedFiltersToListParams,
  resolveFeedFilters,
  type KaiFeedFiltersEventDetail,
} from "@/features/feed/feedKaiFilters";
import { peekFeedPage } from "@/features/feed/feedPageCache";
import { isGraphMailPayload } from "@/features/feed/GraphMailFeedDetail";
import { isBookingOpportunityPayload } from "@/features/feed/FeedBookingOpportunityDetail";
import { isNewBookingMailPayload } from "@/features/feed/NewBookingMailFeedDetail";
import { isClientRestrictedFeedEvent } from "@/features/feed/clientPortalFeed";
import {
  feedEventMatchesType,
  isHiddenFeedEventType,
  isScopeVisibleForRole,
} from "@/features/feed/feedVisuals";
import { feedEventMatchesTextSearch } from "@/features/feed/feedTextSearch";
import { isAdminRole } from "@/lib/userDisplay";
import type {
  FeedEvent,
  FeedEventDetail,
  FeedEventType,
  FeedListParams,
  FeedSeverity,
  ReviewStatus,
  ScopeDimension,
  TimeHorizon,
} from "@/types/feed";

const LAST_SEEN_KEY = "twido:feed:last-seen-at";
const FILTERS_KEY = "twido:feed:filters";

/**
 * The booking `source_id` a per-booking opportunity card is anchored to, used to
 * dedupe stale rebook/first-book roots for the same booking (newest wins). Non
 * opportunity events return `null`.
 */
function bookingOpportunitySourceId(event: FeedEvent): string | null {
  if (!isBookingOpportunityPayload(event.payload)) return null;
  const payload = event.payload;
  const sourceId = (payload.source_id ?? payload.booking?.source_id)?.trim();
  return sourceId ? sourceId : null;
}

function isVisibleFeedEvent(
  event: FeedEvent,
  opts: {
    scopeDimension: ScopeDimension;
    scopeKey?: string;
    eventType?: FeedEventType;
    isAdmin: boolean;
    isClient: boolean;
    mailboxEmails: string[];
    salesPersonLink?: string | null;
    openedByValues: string[];
    searchText?: string;
  },
): boolean {
  if (!feedEventMatchesTextSearch(event, opts.searchText)) return false;
  if (opts.isClient && isClientRestrictedFeedEvent(event)) return false;
  if (isHiddenFeedEventType(event.event_type)) return false;
  // Retired grouped commercial-digest cards are replaced by per-booking
  // opportunity roots — never render a digest-shaped card (belt-and-suspenders
  // with the server-side hide).
  if (event.payload?.kind === "commercial_digest") return false;
  const bookingFirstBook = event.payload?.kind === "booking_first_book";
  if (bookingFirstBook) {
    if (opts.scopeDimension !== "booking" || !opts.scopeKey) return false;
    const ids = event.entity_scope.booking_ids ?? [];
    if (
      event.reference_id !== opts.scopeKey &&
      !ids.includes(opts.scopeKey)
    ) {
      return false;
    }
  } else if (event.causation_id) {
    // Other child effects only appear under an expanded root row.
    return false;
  }
  if (
    opts.eventType &&
    !feedEventMatchesType(event.event_type, opts.eventType, event.payload)
  ) {
    return false;
  }
  const mailboxScoped =
    isGraphMailPayload(event.payload) ||
    isNewBookingMailPayload(event.payload);
  if (opts.scopeDimension !== "my" || !mailboxScoped) {
    return true;
  }
  // Mirrors `graph_mail_visible_on_my_feed`: admins never see mailbox
  // traffic on My Feed; everyone else sees only their own inbox or mail
  // scoped to their sales / Open-By book.
  if (opts.isAdmin) return false;
  const mailbox = (event.payload as { mailbox_email?: string }).mailbox_email;
  if (
    mailbox &&
    opts.mailboxEmails.some((email) => email.toLowerCase() === mailbox.toLowerCase())
  ) {
    return true;
  }
  const salesLink = opts.salesPersonLink?.trim();
  if (salesLink && event.entity_scope.sales_person === salesLink) return true;
  const opener = event.entity_scope.opened_by;
  if (
    opener &&
    opts.openedByValues.some((value) => value.toLowerCase() === opener.toLowerCase())
  ) {
    return true;
  }
  return false;
}

/** The single virtual-filter model shared by every feed surface. */
export interface FeedFilters {
  /** Which virtual dimension to show. `my` is the role-aware default. */
  scopeDimension: ScopeDimension;
  /** Entity key when drilled into a specific booking / customer / lane. */
  scopeKey?: string;
  /** Optional planning-horizon overlay. */
  timeHorizon?: TimeHorizon;
  /** Optional severity overlay (`info` / `warning` / `urgent`). */
  severity?: FeedSeverity;
  /** Optional event-type overlay (`rebook_opportunity`, `first_book`, …). */
  eventType?: FeedEventType;
  /** Personal review filter. `active` (default) hides reviewed events. */
  reviewStatus: ReviewStatus;
  /** Restrict the feed to the caller's favorited ("starred") bookings. */
  favoritesOnly?: boolean;
  /** Free-text filter on event title / summary (server + live stream). */
  searchText?: string;
}

interface FeedContextValue {
  events: FeedEvent[];
  loading: boolean;
  error: string | null;
  /** Count of events created after the user last opened the feed. */
  unreadCount: number;
  /** Mark everything currently in the feed as seen (clears the badge). */
  markAllSeen: () => void;
  /** Refetch the first page from the API with the current filters. */
  refresh: () => Promise<void>;
  /** Load an event's cause → effect detail (cached per event id). */
  getEventDetail: (id: string) => Promise<FeedEventDetail>;
  /** The current virtual-filter model. */
  filters: FeedFilters;
  /** Merge a partial filter change (refetches + reconnects the stream). */
  setFilters: (patch: Partial<FeedFilters>) => void;
  /** Convenience: switch the whole widget to one dimension (+ optional key). */
  setScope: (dimension: ScopeDimension, key?: string) => void;
  /** Reset back to the default My Feed aggregate. */
  resetScope: () => void;
  /** Mark one or more events reviewed for this user (personal declutter). */
  reviewEvents: (ids: string[]) => Promise<void>;
  /** Whether the caller is locked to a personal booking portfolio. */
  isPersonallyScoped: boolean;
}

const FeedContext = createContext<FeedContextValue | null>(null);

function readLastSeen(): number {
  const raw =
    typeof window !== "undefined"
      ? window.localStorage.getItem(LAST_SEEN_KEY)
      : null;
  const parsed = raw ? Date.parse(raw) : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

function filtersToParams(
  filters: FeedFilters,
  intersectPortfolio: boolean,
): FeedListParams {
  return feedFiltersToListParams(filters, undefined, { intersectPortfolio });
}

function applyFilterPatch(
  prev: FeedFilters,
  patch: Partial<FeedFilters>,
): FeedFilters {
  const next = { ...prev, ...patch };
  // Dropping back to My Feed clears any stale drill-down key.
  if (next.scopeDimension === "my" && !("scopeKey" in patch)) {
    next.scopeKey = undefined;
  }
  return next;
}

function filtersEqual(a: FeedFilters, b: FeedFilters): boolean {
  return (
    a.scopeDimension === b.scopeDimension &&
    a.scopeKey === b.scopeKey &&
    a.timeHorizon === b.timeHorizon &&
    a.severity === b.severity &&
    a.eventType === b.eventType &&
    a.reviewStatus === b.reviewStatus &&
    Boolean(a.favoritesOnly) === Boolean(b.favoritesOnly) &&
    (a.searchText?.trim() ?? "") === (b.searchText?.trim() ?? "")
  );
}

function persistFilters(next: FeedFilters) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify(next));
  } catch {
    // Best-effort persistence only.
  }
}

export function FeedProvider({ children }: { children: ReactNode }) {
  const userCtx = useUserOptional();
  const toastApi = useToastOptional();
  const profile = userCtx?.profile ?? null;
  const [error, setError] = useState<string | null>(null);
  const [lastSeenAt, setLastSeenAt] = useState<number>(() => readLastSeen());
  const [filters, setFiltersState] = useState<FeedFilters>(() =>
    resolveFeedFilters(),
  );
  const [events, setEvents] = useState<FeedEvent[]>(() => {
    const warmed = peekFeedPage(feedFiltersToListParams(resolveFeedFilters()));
    return warmed?.data ?? [];
  });
  const [loading, setLoading] = useState(() => {
    const warmed = peekFeedPage(feedFiltersToListParams(resolveFeedFilters()));
    return !warmed;
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const pendingReviewPersists = useRef(new Set<() => Promise<void>>());
  const refreshRequestId = useRef(0);

  // Detail cache so repeated expansions don't refetch the same chain.
  const detailCache = useRef<Map<string, FeedEventDetail>>(new Map());
  // Ids the user reviewed this session — blocks the live stream from re-adding a
  // reviewed event to the Active view before the next refetch.
  const reviewedIds = useRef<Set<string>>(new Set());

  // Notification sound — read via refs so `mergeEvent` stays stable and the SSE
  // stream isn't re-subscribed whenever the theme or preference changes.
  const { theme } = useTheme();
  const { preferences } = useUserPreferences();
  const notificationSoundsRef = useRef(preferences.notificationSounds);
  const themeRef = useRef(theme);
  useEffect(() => {
    notificationSoundsRef.current = preferences.notificationSounds;
  }, [preferences.notificationSounds]);
  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  // Mirror the API's feed lock (see `resolve_caller_feed_scope`): a SalesPerson
  // is always personally scoped — even without a link — and any user with
  // "Open By" assignments is locked to their book. The feed lock is NOT gated on
  // the `scopeToMyBookings` preference (that only governs other surfaces), so the
  // UI stays consistent with what the API actually returns.
  const isPersonallyScoped = useMemo(() => {
    if (!profile) return false;
    if (profile.role === "SalesPerson") return true;
    // A member of any role linked to a sales_person, opener, or agent identity
    // is personally scoped to the UNION of those portfolios (mirrors the API's
    // `resolve_caller_feed_scope`).
    if (profile.sales_person_link?.trim()) return true;
    if ((profile.opened_by_values ?? []).some((value) => value.trim())) return true;
    return (profile.agent_values ?? []).some((value) => value.trim());
  }, [profile]);

  const isAdmin = isAdminRole(profile?.role);
  const isClient = profile?.role === "Client";
  const mailboxEmails = useMemo(() => {
    const email = profile?.email?.trim();
    return email ? [email] : [];
  }, [profile?.email]);
  const openedByValues = profile?.opened_by_values ?? [];
  const salesPersonLink = profile?.sales_person_link;
  const viewerScope = useViewerBookingScope();
  const intersectPortfolio = hasViewerScope(viewerScope);

  const mergeEvent = useCallback(
    (incoming: FeedEvent) => {
      if (reviewedIds.current.has(incoming.id)) return;
      if (
        !isVisibleFeedEvent(incoming, {
          scopeDimension: filters.scopeDimension,
          scopeKey: filters.scopeKey,
          eventType: filters.eventType,
          isAdmin,
          isClient,
          mailboxEmails,
          salesPersonLink,
          openedByValues,
          searchText: filters.searchText,
        })
      ) {
        return;
      }
      setEvents((prev) => {
        if (prev.some((e) => e.id === incoming.id)) return prev;
        // Genuinely new live event — ping (guards enforced in the helper).
        playNotificationSoundForEvent(
          themeRef.current,
          notificationSoundsRef.current,
        );
        // Per-booking opportunity roots supersede older rebook/first-book cards
        // for the same booking (the SSE event is the newest cycle), mirroring the
        // server-side list dedupe so the live feed shows one card per source_id.
        const incomingSource = bookingOpportunitySourceId(incoming);
        const deduped = incomingSource
          ? prev.filter(
              (e) => bookingOpportunitySourceId(e) !== incomingSource,
            )
          : prev;
        // Keep newest-first ordering; the feed API and SSE both emit roots.
        return [incoming, ...deduped].slice(0, 100);
      });
    },
    [
      filters.scopeDimension,
      filters.scopeKey,
      filters.eventType,
      filters.searchText,
      isAdmin,
      isClient,
      mailboxEmails,
      salesPersonLink,
      openedByValues,
    ],
  );

  const refresh = useCallback(async () => {
    const requestId = ++refreshRequestId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getFeed(filtersToParams(filters, intersectPortfolio));
      if (requestId !== refreshRequestId.current) return;
      setEvents(
        (res.data ?? []).filter((event) =>
          isVisibleFeedEvent(event, {
            scopeDimension: filters.scopeDimension,
            scopeKey: filters.scopeKey,
            eventType: filters.eventType,
            isAdmin,
            isClient,
            mailboxEmails,
            salesPersonLink,
            openedByValues,
            searchText: filters.searchText,
          }),
        ),
      );
    } catch (err) {
      if (requestId !== refreshRequestId.current) return;
      setError(err instanceof Error ? err.message : "Failed to load feed");
    } finally {
      if (requestId === refreshRequestId.current) setLoading(false);
    }
  }, [
    filters,
    intersectPortfolio,
    isAdmin,
    isClient,
    mailboxEmails,
    salesPersonLink,
    openedByValues,
  ]);

  // Refetch whenever the profile becomes ready or the filters change.
  useEffect(() => {
    if (!profile) return;
    void refresh();
  }, [profile, refresh]);

  // Live SSE subscription with reconnect. Re-subscribes when filters change so
  // the stream stays in sync with the current view. While the tab is hidden the
  // stream is dropped (no point holding a connection + processing pushes the
  // user can't see); returning to the tab refetches the first page to catch up
  // on anything missed, then re-subscribes.
  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    let controller: AbortController | null = null;
    const params = filtersToParams(filters, intersectPortfolio);

    const run = async () => {
      while (!cancelled) {
        if (document.hidden) {
          // Wait for the tab to become visible again before (re)connecting.
          await new Promise<void>((resolve) => {
            const onVisible = () => {
              if (!document.hidden) {
                document.removeEventListener("visibilitychange", onVisible);
                resolve();
              }
            };
            document.addEventListener("visibilitychange", onVisible);
          });
          if (cancelled) break;
          // Catch up on events that arrived while we were disconnected.
          void refresh();
        }

        controller = new AbortController();
        try {
          await api.streamFeed(
            (event) => mergeEvent(event),
            controller.signal,
            params,
          );
        } catch {
          // Network hiccup or token refresh — fall through to backoff below.
        }
        if (cancelled) break;
        // Backoff before reconnecting so a persistent failure doesn't spin.
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
    };

    const onVisibilityChange = () => {
      // Drop the live connection as soon as the tab is backgrounded; the loop
      // parks on the visibility wait above until we return.
      if (document.hidden) controller?.abort();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    void run();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      controller?.abort();
    };
  }, [profile, mergeEvent, filters, refresh, intersectPortfolio]);

  const setFilters = useCallback((patch: Partial<FeedFilters>) => {
    const next = applyFilterPatch(filtersRef.current, patch);
    if (filtersEqual(filtersRef.current, next)) return;
    persistFilters(next);
    filtersRef.current = next;
    // Invalidate any in-flight fetch so a slower previous filter can't land
    // after the user has already switched. Clear rows in this click so the
    // chip and the list enter the loading state on the same render.
    refreshRequestId.current += 1;
    setEvents([]);
    setLoading(true);
    setError(null);
    setFiltersState(next);
  }, []);

  const setScope = useCallback(
    (dimension: ScopeDimension, key?: string) => {
      setFilters({ scopeDimension: dimension, scopeKey: key });
    },
    [setFilters],
  );

  const resetScope = useCallback(() => {
    setFilters({ scopeDimension: "my", scopeKey: undefined });
  }, [setFilters]);

  // Kai re-scopes the feed on `/feed` by dispatching `kai-feed-filters` (see
  // `feedKaiFilters.ts`). Apply the resolved scope through the same setters the
  // UI uses so every feed surface stays consistent.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<KaiFeedFiltersEventDetail>).detail;
      const next = detail?.filters;
      if (!next) return;
      if (
        next.scopeDimension === "my" &&
        !next.scopeKey &&
        !next.timeHorizon &&
        !next.severity
      ) {
        resetScope();
        return;
      }
      setFilters({
        scopeDimension: next.scopeDimension,
        scopeKey: next.scopeKey,
        timeHorizon: next.timeHorizon,
        severity: next.severity,
      });
    };
    window.addEventListener(KAI_FEED_FILTERS_EVENT, handler);
    return () => window.removeEventListener(KAI_FEED_FILTERS_EVENT, handler);
  }, [setFilters, resetScope]);

  // Deep-link / session may restore a dimension this role must not see
  // (User → operator while scoped to my bookings, SalesPerson → sales_person).
  // Fall back to My Feed.
  useEffect(() => {
    if (!profile) return;
    const hasAgentAssignment = (profile.agent_values?.length ?? 0) > 0;
    if (
      !isScopeVisibleForRole(
        filters.scopeDimension,
        profile.role,
        hasAgentAssignment,
        preferences.scopeToMyBookings,
      )
    ) {
      resetScope();
    }
  }, [
    profile,
    filters.scopeDimension,
    preferences.scopeToMyBookings,
    resetScope,
  ]);

  const markAllSeen = useCallback(() => {
    const now = Date.now();
    setLastSeenAt(now);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LAST_SEEN_KEY, new Date(now).toISOString());
    }
  }, []);

  const getEventDetail = useCallback(
    async (id: string) => {
      const cached = detailCache.current.get(id);
      if (cached) return cached;
      const scopeParams = filtersToParams(filters, intersectPortfolio);
      const detail = await api.getFeedEvent(id, {
        scope_dimension: scopeParams.scope_dimension,
        scope_key: scopeParams.scope_key,
        portfolio_intersection: scopeParams.portfolio_intersection,
      });
      detailCache.current.set(id, detail);
      return detail;
    },
    [filters, intersectPortfolio],
  );

  const reviewEvents = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      // On the decluttered Active view a reviewed event leaves the list; on the
      // All / Reviewed views it stays put but gains the reviewed marker.
      const removeFromList = filters.reviewStatus === "active";
      const reviewedAt = new Date().toISOString();
      const snapshot = snapshotFeedEvents(eventsRef.current, ids);
      if (removeFromList) {
        ids.forEach((id) => reviewedIds.current.add(id));
      }
      setEvents((prev) =>
        applyFeedReview(prev, ids, { removeFromList, reviewedAt }),
      );

      let settled = false;
      const persist = async () => {
        if (settled) return;
        settled = true;
        pendingReviewPersists.current.delete(persist);
        try {
          await api.reviewFeedEvents(ids);
        } catch (err) {
          if (removeFromList) {
            ids.forEach((id) => reviewedIds.current.delete(id));
          }
          setEvents((prev) => restoreFeedReview(prev, snapshot));
          emitFeedReviewUndo(ids);
          toastApi?.toast({
            title: "Couldn't mark as reviewed",
            description:
              err instanceof Error ? err.message : "Please try again.",
          });
          await refresh();
          throw err;
        }
      };
      const undo = () => {
        if (settled) return;
        settled = true;
        pendingReviewPersists.current.delete(persist);
        if (removeFromList) ids.forEach((id) => reviewedIds.current.delete(id));
        setEvents((prev) => restoreFeedReview(prev, snapshot));
        emitFeedReviewUndo(ids);
      };
      pendingReviewPersists.current.add(persist);

      if (toastApi) {
        toastApi.toast({
          title: feedReviewToastTitle(ids.length),
          action: { label: "Undo", onAction: undo },
          onCommit: () => {
            void persist();
          },
        });
        return;
      }

      await persist();
    },
    [filters.reviewStatus, refresh, toastApi],
  );

  useEffect(() => {
    const pending = pendingReviewPersists.current;
    return () => {
      for (const persist of pending) void persist();
    };
  }, []);

  const unreadCount = events.reduce(
    (n, e) => (Date.parse(e.created_at) > lastSeenAt ? n + 1 : n),
    0,
  );

  return (
    <FeedContext.Provider
      value={{
        events,
        loading,
        error,
        unreadCount,
        markAllSeen,
        refresh,
        getEventDetail,
        filters,
        setFilters,
        setScope,
        resetScope,
        reviewEvents,
        isPersonallyScoped,
      }}
    >
      {children}
    </FeedContext.Provider>
  );
}

export function useFeed(): FeedContextValue {
  const ctx = useContext(FeedContext);
  if (!ctx) {
    throw new Error("useFeed must be used within a FeedProvider");
  }
  return ctx;
}

/** Optional feed context — for surfaces that may render outside the shell provider. */
export function useFeedOptional(): FeedContextValue | null {
  return useContext(FeedContext);
}
